import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n";
import { ArrowRight, CheckCircle2, AlertTriangle, FileText, Globe2, Car } from "lucide-react";

/**
 * Bilingual SEO landing page explaining how to drive in the US for foreign
 * visitors — especially focused on Brazilians (CNH, IDP, tolls, parking).
 * Designed to rank for queries like:
 *   - "alugar carro com CNH brasileira em Boston"
 *   - "International Driving Permit Massachusetts"
 *   - "documentos para dirigir nos EUA"
 */
const DrivingDocs = () => {
  const { lang } = useI18n();
  const isPt = lang === "pt";

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-12 py-20" data-testid="driving-docs-page">
      <p className="text-label text-[#FFB400]">
        <span className="line-accent" />
        {isPt ? "Guia oficial Movix" : "Official Movix guide"}
      </p>
      <h1 className="mt-4 font-heading text-5xl sm:text-6xl font-bold tracking-tighter text-white">
        {isPt
          ? "Documentos para dirigir nos EUA"
          : "Documents to drive in the United States"}
      </h1>
      <p className="mt-6 text-zinc-300 text-lg leading-relaxed">
        {isPt
          ? "Recém-chegou em Boston e quer dirigir? Aqui está o guia direto ao ponto: o que a Movix exige, o que a polícia de Massachusetts aceita e o que você NÃO precisa fazer (mesmo que outros sites digam que precisa)."
          : "Just arrived in Boston and need to drive? Here is the straight-to-the-point guide: what Movix requires, what Massachusetts police accept, and what you do NOT need (even if other websites say otherwise)."}
      </p>

      {/* Quick facts strip */}
      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {[
          {
            icon: CheckCircle2,
            t: isPt ? "CNH brasileira" : "Brazilian license",
            v: isPt ? "Aceita por 1 ano" : "Accepted for 1 year",
            color: "text-[#00d96b]",
          },
          {
            icon: AlertTriangle,
            t: "IDP",
            v: isPt
              ? "Recomendada, não obrigatória na Movix"
              : "Recommended, not required at Movix",
            color: "text-[#FFB400]",
          },
          {
            icon: FileText,
            t: isPt ? "Passaporte" : "Passport",
            v: isPt ? "Obrigatório no balcão" : "Required at handover",
            color: "text-white",
          },
        ].map((q, i) => (
          <div key={i} className="bg-[#0a0a0a] p-6">
            <q.icon size={22} className={q.color} />
            <p className="mt-3 text-label">{q.t}</p>
            <p className="mt-1 font-heading text-lg text-white">{q.v}</p>
          </div>
        ))}
      </div>

      {/* Sections */}
      <div className="prose prose-invert max-w-none mt-16 space-y-12">
        <Section
          n="01"
          title={isPt ? "O que você precisa para alugar na Movix" : "What you need to rent at Movix"}
        >
          <ul className="list-none space-y-3 mt-4">
            <Bullet>
              {isPt
                ? "CNH brasileira (ou de qualquer país) válida — sem precisar ser IDP"
                : "Valid Brazilian (or any country's) driver license — no IDP needed"}
            </Bullet>
            <Bullet>{isPt ? "Passaporte" : "Passport"}</Bullet>
            <Bullet>
              {isPt
                ? "Cartão de crédito internacional no nome do motorista (Visa, Mastercard, Amex)"
                : "International credit card in the driver's name (Visa, Mastercard, Amex)"}
            </Bullet>
            <Bullet>
              {isPt
                ? "Idade mínima: 21 anos (motoristas entre 21–24 pagam taxa de $15/dia)"
                : "Minimum age: 21 (drivers 21–24 pay a $15/day young-driver fee)"}
            </Bullet>
            <Bullet>
              {isPt
                ? "Endereço onde você está hospedado (hotel, Airbnb ou endereço fixo)"
                : "Address where you are staying (hotel, Airbnb, or permanent address)"}
            </Bullet>
          </ul>
        </Section>

        <Section
          n="02"
          title={isPt ? "CNH brasileira é válida nos EUA?" : "Is the Brazilian license valid in the US?"}
        >
          <p className="text-zinc-300 leading-relaxed">
            {isPt
              ? "Sim. A CNH brasileira é aceita em todos os estados americanos por motoristas turistas durante o período do visto B1/B2 ou até 1 ano após chegada — o que ocorrer primeiro. Em Massachusetts, residentes (com green card, visto de trabalho ou estudo prolongado) precisam tirar a CDL local em até 6 meses, mas turistas e visitantes podem usar a CNH normalmente."
              : "Yes. The Brazilian driver license is accepted in all US states for tourist drivers during the validity of a B1/B2 visa or up to 1 year after arrival — whichever comes first. In Massachusetts, residents (green card, work or long-term study visa holders) must obtain a local CDL within 6 months, but tourists and visitors can use the Brazilian license normally."}
          </p>
        </Section>

        <Section
          n="03"
          title={isPt ? "Preciso da Permissão Internacional (IDP)?" : "Do I need an International Driving Permit (IDP)?"}
        >
          <p className="text-zinc-300 leading-relaxed">
            {isPt
              ? "Não para alugar na Movix. Algumas locadoras grandes (Hertz, Enterprise, Avis) pedem porque a CNH é em português e os atendentes não entendem. A Movix tem equipe que lê português, então a CNH original basta. A IDP é útil se você for parado pela polícia em outros estados — mas mesmo assim, vale apresentar junto, não substitui."
              : "Not to rent at Movix. Some large chains (Hertz, Enterprise, Avis) require it because the license is in Portuguese and staff cannot read it. Movix has Portuguese-reading staff, so the original license is enough. The IDP is useful when stopped by police in other states — but it is a translation, not a replacement."}
          </p>
        </Section>

        <Section
          n="04"
          title={isPt ? "Pedágios em Massachusetts e na região" : "Tolls in Massachusetts and the region"}
        >
          <p className="text-zinc-300 leading-relaxed">
            {isPt
              ? "Massachusetts usa o sistema EZ-Pass com leitura automática — não há cabines de cobrança em dinheiro. Se você passar sem transponder, a placa do carro é fotografada e o valor é cobrado da locadora, que repassa para você com uma taxa administrativa de $5 a $25 por passagem."
              : "Massachusetts uses the all-electronic EZ-Pass system — there are no cash booths. If you drive through without a transponder, the license plate is photographed and billed to the rental company, which passes it on to you with a $5 to $25 administrative fee per crossing."}
          </p>
          <div className="mt-4 bg-[#FFB400]/10 border border-[#FFB400]/30 p-4">
            <p className="text-sm text-[#FFB400] font-mono-ui uppercase tracking-widest">
              {isPt ? "Dica Movix" : "Movix tip"}
            </p>
            <p className="mt-2 text-zinc-200 text-sm">
              {isPt
                ? "Adicione o EZ-Pass Movix por $5/dia no checkout — pedágios cobrados pelo valor real, sem taxa administrativa de $25."
                : "Add Movix EZ-Pass for $5/day at checkout — tolls billed at face value, no $25 admin fee."}
            </p>
          </div>
        </Section>

        <Section
          n="05"
          title={isPt ? "Estacionamento em Boston" : "Parking in Boston"}
        >
          <ul className="list-none space-y-3 mt-4">
            <Bullet>
              {isPt
                ? "Parquímetros: aceitam apenas cartão ou app ParkBoston (instale antes de chegar)"
                : "Street meters: card-only or ParkBoston app (install before you arrive)"}
            </Bullet>
            <Bullet>
              {isPt
                ? "Limpeza de rua: dia/horário fixo por bairro — se ignorar, o carro é guinchado ($150+ taxa + reboque)"
                : "Street cleaning: fixed day/hour per neighborhood — ignore it and the car is towed ($150+ plus tow)"}
            </Bullet>
            <Bullet>
              {isPt
                ? "Vagas de morador: pintadas em branco com placa 'Resident Permit Required' — NUNCA pare"
                : "Resident-only spots: white-painted with 'Resident Permit Required' signs — never park there"}
            </Bullet>
            <Bullet>
              {isPt
                ? "Garagens: $30–$50/dia no centro; pré-reserva em SpotHero ou Park Whiz reduz pra ~$15"
                : "Garages: $30–$50/day downtown; pre-booking on SpotHero or ParkWhiz cuts it to ~$15"}
            </Bullet>
          </ul>
        </Section>

        <Section
          n="06"
          title={isPt ? "O que fazer se a polícia te parar" : "What to do if the police pull you over"}
        >
          <ol className="list-decimal pl-6 text-zinc-300 leading-relaxed space-y-2 mt-4">
            <li>{isPt ? "Mantenha as mãos no volante, em local visível." : "Keep your hands on the steering wheel, in plain sight."}</li>
            <li>{isPt ? "Não saia do carro a não ser que o policial peça." : "Do not leave the car unless the officer asks."}</li>
            <li>{isPt ? "Apresente: CNH original, passaporte, contrato de aluguel (no porta-luvas)." : "Show: original driver license, passport, rental agreement (in the glove box)."}</li>
            <li>{isPt ? "Fale calmamente em inglês ou português. Se não souber, peça intérprete." : "Speak calmly in English or Portuguese. If you don't know either, ask for an interpreter."}</li>
            <li>{isPt ? "Multa? Você recebe um ticket. Pague online em até 21 dias para evitar acréscimo." : "Ticket? Pay online within 21 days to avoid late fees."}</li>
          </ol>
        </Section>
      </div>

      <div className="mt-20 border-t border-white/10 pt-12 flex flex-wrap items-center justify-between gap-6">
        <div>
          <p className="text-label text-[#FFB400]">
            <Globe2 size={11} className="inline mr-1 -mt-0.5" />
            {isPt ? "Está pronto?" : "Ready?"}
          </p>
          <h2 className="mt-2 font-heading text-3xl font-semibold text-white max-w-lg">
            {isPt
              ? "A frota está esperando você — escolha o seu carro."
              : "The fleet is waiting — pick your car."}
          </h2>
        </div>
        <Link to="/fleet" className="btn-primary glow-coral" data-testid="docs-cta-fleet">
          <Car size={16} /> {isPt ? "Ver a frota" : "See the fleet"} <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
};

const Section = ({ n, title, children }) => (
  <section>
    <p className="text-label text-[#FFB400]">{n}</p>
    <h2 className="mt-2 font-heading text-3xl font-semibold text-white tracking-tight">
      {title}
    </h2>
    {children}
  </section>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-3 text-zinc-300 leading-relaxed">
    <CheckCircle2 size={18} className="text-[#FFB400] mt-0.5 flex-shrink-0" />
    <span>{children}</span>
  </li>
);

export default DrivingDocs;
